import type { ProductListRow } from "@stackje/site-builder";
import { createHttpOwnerListsApi } from "../../api/ownerListsApi";
import { toggleRows } from "../../hooks/useOwnerPage";
import { useMemo, useRef, useState } from "react";
import { ConfirmDialog, EmptyState, FilterBar, FilterSelect, ListHead, SearchField, SelectionBar, AreaError } from "../area";
import type { ShopApi, ShopIndex } from "../../api/contract";
import { ShopRefusedError } from "../../api/errors";
import { newShopId } from "../../lib/shop";
import { NewProductForm } from "./NewProductForm";
import { ProductTable } from "./ProductTable";

import { useShopPage } from "./useShopPage";
import { shopRequest, ShopTimeoutError } from "./shopRequest";
import { ProductListSkeleton, ShopActivity, ShopLoadError, ShopMore } from "./ShopLoading";

type BulkAction = "available" | "hidden" | "delete";

const STATUS_OPTIONS = [{ value: "any", label: "Status: Any" }, { value: "available", label: "Available" }, { value: "hidden", label: "Hidden" }, { value: "sold-out", label: "Sold out" }];

const BULK_COPY: Record<BulkAction, { title: (count: number) => string; text: string; confirm: string }> = {
  available: { title: (count) => `Make ${count === 1 ? "this product" : `these ${count} products`} available?`, text: "They appear in your shop, and saved changes reach buyers straight away.", confirm: "Make available" },
  hidden: { title: (count) => `Hide ${count === 1 ? "this product" : `these ${count} products`}?`, text: "They leave shop lists and search. Your products are kept.", confirm: "Hide" },
  delete: { title: (count) => `Delete ${count === 1 ? "this product" : `these ${count} products`}?`, text: "They leave the catalogue.", confirm: "Delete" },
};

/** The Products screen: find products, add one, and change several at once. */
export function ProductsList({ api, siteId, shop, language, reload, onOpen }: { api: ShopApi; siteId: string; shop: ShopIndex; language: string; reload: () => Promise<void>; onOpen: (id: string) => void }) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("any");
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<ProductListRow[]>([]);
  const [confirm, setConfirm] = useState<BulkAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lists = useMemo(() => api.lists ?? createHttpOwnerListsApi(), [api]);
  const [sort, setSort] = useState("oldest");
  const page = useShopPage(lists.products, siteId, { search, category, status, sort }, shop);
  const rows = page.rows;
  const working = useRef(false);
  const categoryOptions = [{ value: "", label: "All categories" }, ...shop.categories.map((item) => ({ value: item.id, label: item.name }))];

  const work = async (action: () => Promise<void>): Promise<void> => {
    if (working.current) return; working.current = true;
    setBusy(true); setError(null);
    try { await shopRequest(() => action()); await shopRequest(() => reload()); }
    catch (caught) { setError(caught instanceof ShopRefusedError || caught instanceof ShopTimeoutError ? caught.message : "This didn't save. Reload and try again."); }
    finally { working.current = false; setBusy(false); }
  };
  const selectedProducts = () => selected;

  const create = (name: string, slug: string) => work(async () => {
    const created = await api.createProduct(siteId, { id: newShopId("product"), name, slug });
    setAdding(false);
    onOpen(created.id);
  });
  const setCategoryForSelected = (categoryId: string) => work(async () => {
    for (const item of selectedProducts()) await api.saveProduct(siteId, item.id, item.rowVersion, { categories: [categoryId] });
    setSelected([]);
  });
  const applyBulk = (action: BulkAction) => work(async () => {
    for (const item of selectedProducts()) {
      if (action === "delete") await api.deleteProduct(siteId, item.id, item.rowVersion);
      else await api.setProductAvailability(siteId, item.id, item.rowVersion, action);
    }
    setSelected([]);
    setConfirm(null);
  });

  return <div aria-busy={busy || page.busy}>
    <ListHead title="Products" action={{ label: "New product", onClick: () => setAdding(true), disabled: busy }} />
    {adding && <NewProductForm busy={busy} onCancel={() => { if (!busy) setAdding(false); }} onCreate={create} />}
    <FilterBar fields>
      <SearchField label="Search products" value={search} onChange={setSearch} />
      <FilterSelect label="Filter by category" value={category} onChange={setCategory} options={categoryOptions} />
      <FilterSelect label="Filter by status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
      <FilterSelect label="Sort products" value={sort} onChange={setSort} options={[{ value: "oldest", label: "Oldest first" }, { value: "newest", label: "Newest first" }, { value: "name", label: "Name" }, { value: "price-low", label: "Price: Low to high" }, { value: "price-high", label: "Price: High to low" }]} />
    </FilterBar>
    <div className="bw-shop-refresh" data-refreshing={page.busy && rows.length > 0} aria-busy={page.busy}>
    {page.busy && rows.length > 0 && <ShopActivity />}
    <div className="bw-shop-rows">{rows.length
      ? <ProductTable rows={rows} siteId={siteId} shop={shop} language={language} selected={selected.map(r => r.id)} onSelected={(changed, checked) => setSelected(before => toggleRows(before, changed, checked))} onOpen={onOpen} />
      : page.busy ? <ProductListSkeleton /> : page.error ? null : <EmptyState title={(shop.total ?? shop.products.length) ? "No matching products" : "Your first product"}>{(shop.total ?? shop.products.length) ? "Try another search or filter." : "Add products and downloads here. New products start hidden."}</EmptyState>}</div></div>
    <ShopMore shown={rows.length} total={page.total} hasMore={page.hasMore} busy={page.busy} onMore={page.more} />
    {page.error && <ShopLoadError message={page.error} retry={page.retry} />}
    {selected.length > 0 && <SelectionBar count={selected.length} onClear={() => setSelected([])}>
      <select aria-label="Set category for selected products" disabled={busy} value="" onChange={(event) => { if (event.target.value) void setCategoryForSelected(event.target.value); }}>
        <option value="">Set category…</option>
        {shop.categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select>
      <button type="button" className="bw-btn bw-btn--text" disabled={busy} onClick={() => setConfirm("available")}>Make available</button>
      <button type="button" className="bw-btn bw-btn--text" disabled={busy} onClick={() => setConfirm("hidden")}>Hide</button>
      <button type="button" className="bw-btn bw-btn--text" data-tone="danger" disabled={busy} onClick={() => setConfirm("delete")}>Delete</button>
    </SelectionBar>}
    {confirm && <ConfirmDialog title={BULK_COPY[confirm].title(selected.length)} confirmLabel={busy ? "Working…" : BULK_COPY[confirm].confirm} tone={confirm === "delete" ? "danger" : "action"} busy={busy} onCancel={() => { if (!busy) setConfirm(null); }} onConfirm={() => void applyBulk(confirm)}>
      {busy && <ShopActivity label="Working…" />}<p className="bw-area-dialog-lead">{BULK_COPY[confirm].text}</p>
    </ConfirmDialog>}
    {error && <AreaError message={error} onDismiss={() => setError(null)} />}
  </div>;
}
